/* tslint:disable:no-console */
import Vue from 'vue';
import Vuex, { Store, Module } from 'vuex';
import VuexPersistence from 'vuex-persist';
import localForage from 'localforage';
import router from './router';

Vue.use(Vuex);

// variables and helper functions
interface INameToValueMap {
  [key: string]: any;
}
function randomIntFromInterval(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1) + min);
}
function pad(n: any, width: any, z: any) {
  z = z || '0';
  n = n + '';
  return n.length >= width ? n : new Array(width - n.length + 1).join(z) + n;
}
function parseJwt(token: string) {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
  }).join(''));
  return JSON.parse(jsonPayload);
}
function formatDate(date: string) {
  if (!date) {
    return null;
  }
  const [year, month, day] = date.split('-');
  return `${day}.${month}.${year}`;
}
function parseDate(date: string) {
  if (!date) {
    return null;
  }
  const [day, month, year] = date.split('.');
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
}

const vuexLocalforage = new VuexPersistence({
  storage: localForage,
  asyncStorage: true,
  modules: ['app'],
});

// store module that keeps basic app state and simple data
const appModule = {
  state: {
    drawerLeft: false,
    userSettings: {
      stayLoggedIn: false,
      denseRows: false,
    },
    showSearchModal: false,
    userAuthenticated: false,
    userTokenPayload: {},
    username: '',
    jwt: '',
  },
  mutations: {
    toggleDrawerLeft(state: any) {
      state.drawerLeft = !state.drawerLeft;
    },
    setUserSetting(state: any, obj: any) {
      state.userSettings[obj.key] = obj.val;
    },
    toggleSearchModal(state: any, val: boolean) {
      state.showSearchModal = val;
    },
    /**
     * Set JWT token and payload to store
     * @param state
     * @param token
     */
    setJWT(state: any, token: string) {
      state.jwt = token;
      const payload = parseJwt(token);
      state.userTokenPayload = payload;
      state.username = payload.username;
    },
    /**
     * Set user authenticated
     * @param state
     * @param isValidUser
     */
    setUserAuthenticated(state: any, isValidUser: boolean) {
      state.userAuthenticated = isValidUser;
      if (!isValidUser) {
        state.username = '';
        state.userTokenPayload = {};
        state.jwt = '';
      }
    },
  },
  actions: {
  },
};

// store module that allows accessing list data
const listModule = {
  state: {
    list: [],
    detail: null,
    lockedStates: {},
    updateCounter: 0,
    hasNewItems: true,
    isProcessing: false,
    currentProgress: 0,
    showDetailId: null,
    dialog: false,
    dialogMessage: '',
    filters: {
      query: '',
      searchString: '',
      from: null,
      to: null,
      pdfs: true,
      services: [],
    },
    tableHeaders: [
      { text: 'Datum', value: 'data.sent', id: 'sent', sortable: false },
      { text: 'Überschrift', value: 'data.headline', id: 'headline', sortable: false },
      { text: 'Dienst', value: 'data.svc_name', id: 'svc_name', sortable: false },
      // { text: 'Res', value: 'data.cat', id: 'cat', sortable: false },
    ],
    services: [
      { value: 'bas', text: 'Basisdienst' },
      { value: 'fea', text: 'Featuredienst' },
      { value: 'dok', text: 'Dokumentation' },
      { value: 'med', text: 'Medien' },
      { value: 'soz', text: 'Sozial' },
      { value: 'wos', text: 'Wochenspiegel' },
      { value: 'fil', text: 'Film' },
      { value: 'lbm', text: 'Landesdienst Bayern' },
      { value: 'lmw', text: 'Landesdienst Mitte-West' },
      { value: 'lnb', text: 'Landesdienst Niedersachsen-Bremen' },
      { value: 'lnh', text: 'Landesdienst Nord' },
      { value: 'lob', text: 'Landesdienst Ost' },
      { value: 'lbw', text: 'Landesdienst Südwest' },
      { value: 'lwd', text: 'Landesdienst West' },
    ],
  },
  mutations: {
    /**
     * New listitems are available
     * @param state
     */
    newItems(state: any, val: boolean) {
      state.hasNewItems = val;
    },
    /**
     * Data is being processed
     * @param state
     */
    isProcessing(state: any, val: boolean) {
      state.isProcessing = val;
    },
    /**
     * Populates the Listview Array
     * @param state
     * @param data
     */
    populateList(state: any, data: any) {
      if (data && data.length >= 0) {
        state.list = data;
      }
    },
    /**
     * Programatically trigger showing another detail
     * @param state
     * @param id
     */
    showDetail(state: any, id: any) {
      state.showDetailId = id;
    },
    /**
     * Programatically toggles dialog
     * @param state
     * @param data
     */
    toggleDialog(state: any, data: any) {
      state.dialog = data.viz || false;
      state.dialogMessage = data.msg || '';
    },
    /**
     * Populates the Listview Detail object
     * @param state
     * @param data
     */
    populateItem(state: any, data: any) {
      state.detail = data;
    },
    /**
     * Updates sync progress value
     * @param state
     * @param val
     */
    updateProgress(state: any, val: number) {
      state.currentProgress = val;
    },
    /**
     * Updates search parameters
     * @param state
     * @param data
     */
    updateSearchparameters(state: any, data: any) {
      if (!data.key) { return; }
      state.filters[data.key] = data.val;

      // build and compare searchString
      if (data.key !== 'searchString') {
        const f: any = state.filters;
        const str: any = [
          f.query,
          f.from !== null ? 'von:' + formatDate(f.from) : null,
          f.to !== null ? 'bis:' + formatDate(f.to) : null,
          f.services.length > 0 ? 'Dienste:' + f.services.join(',') : null,
        ].filter(Boolean).join(' ');

        if (str !== f.searchString) {
          f.searchString = str;
        }
      } else {
        state.filters = {
          query: '',
          searchString: data.val,
          from: null,
          to: null,
          pdfs: state.filters.pdfs,
          services: [],
        };
      }
    },
  },
  actions: {
    /**
     * Parses the search parameters and query string to prepare search
     * @param context
     * @param source
     */
    performSearch(context: any, source: string) {
      // build and compare searchString
      const filters: any = context.rootState.list.filters;
      const query: string = context.rootState.list.filters.searchString;
      const params: any = {};

      // check if query contains complex parameters
      if (query.indexOf(':') >= 0) {
        const parts = query.split(' ');
        const searchString = parts.map((part) => {
          // extract searchstring and parse search parameters
          if (part.indexOf(':') >= 0) {
            const kv = part.split(':');
            const key = kv[0].toLowerCase();
            const dateRegex1 = /^(0[1-9]|[12][0-9]|3[01])[- /.](0[1-9]|1[012])[- /.]((19|20)\d\d)$/;
            const dateRegex2 = /^(19|20)\d\d[- /.](0[1-9]|1[012])[- /.](0[1-9]|[12][0-9]|3[01])$/;
            const services = context.rootState.list.services.map((svc: any) => svc.value);

            // from
            if (key.indexOf('von') >= 0 || key.indexOf('from') >= 0) {
              if (kv[1].match(dateRegex1)) {
                const tmp = kv[1].match(dateRegex1);
                if (tmp) {
                  params.from = tmp[3] + '-' + tmp[2] + '-' + tmp[1];
                }
              } else if (kv[1].match(dateRegex2)) {
                params.from = kv[1];
              }
            // to
            } else if (key.indexOf('bis') >= 0 || key.indexOf('to') >= 0) {
              if (kv[1].match(dateRegex1)) {
                const tmp = kv[1].match(dateRegex1);
                if (tmp) {
                  params.to = tmp[3] + '-' + tmp[2] + '-' + tmp[1];
                }
              } else if (kv[1].match(dateRegex2)) {
                params.to = kv[1];
              }
            // services
            } else if (key.indexOf('svc') >= 0 || key.indexOf('dienst') >= 0) {
              params.svc_name = kv[1];
              if (params.svc_name.indexOf(',')) {
                params.svc_name = params.svc_name.split(',').filter((str: string) => {
                  if (services.indexOf(str) >= 0) {
                    return str;
                  }
                });
              } else {
                params.svc_name = [params.svc_name];
              }
            }
          } else {
            return part;
          }
        }).filter(Boolean).join(' ');
        params.pdfs = filters.pdfs;
        params.query = searchString;

      } else {
        // no advanced search parameters in string, try looking in filter state
        params.query = query;
        params.from = filters.from;
        params.to = filters.to;
        params.pdfs = filters.pdfs;
        params.svc_name = filters.services;
      }

      context.commit('updateSearchparameters', {key: 'query', val: params.query || ''});
      context.commit('updateSearchparameters', {key: 'from', val: params.from || null});
      context.commit('updateSearchparameters', {key: 'to', val: params.to || null});
      context.commit('updateSearchparameters', {key: 'pdfs', val: params.pdfs});
      context.commit('updateSearchparameters', {key: 'services', val: params.svc_name || []});

      context.dispatch('triggerSearch', 'performSearch');
    },
    /**
     * Toggles webarc_hide for doc.
     * @param context
     * @param data
     */
    async hideDoc(context: any, data: any) {
      const URL = process.env.VUE_APP_AUTH_URL;
      const response = await fetch(URL + '/api/hide', {
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify(data),
        headers: {
            'Authorization': 'Bearer ' + context.rootState.app.jwt,
            'Content-Type': 'application/json',
        },
      });
      try {
        if (response.status === 401) {
          context.commit('setUserAuthenticated', false);
          if (router.currentRoute.path !== '/') {
            router.push('/');
          }
        }
        return response;
      } catch (err) {
        console.error(err);
      }
    },
    /**
     * Triggers the search based on the current search criteria.
     * @param context
     * @param from
     */
    async triggerSearch(context: any, from: string) {
      context.commit('newItems', true);
      const URL = process.env.VUE_APP_AUTH_URL;
      const filters = context.rootState.list.filters;
      const body = {
        from: filters.from !== 'null' ? filters.from : null,
        to: filters.to !== 'null' ? filters.to : null,
        pdfs: filters.pdfs !== 'null' ? filters.pdfs : true,
        services: filters.services.length > 0 ? filters.services : null,
        query: filters.query,
      };
      const controller = new AbortController();
      const signal = controller.signal;

      try {
        // 22 second timeout, then cancel request
        const timeoutId = setTimeout(() => {
          controller.abort();
        }, 22000);

        const response = await fetch(URL + '/api/list', {
          method: 'POST',
          credentials: 'include',
          body: JSON.stringify(body),
          signal,
          headers: {
              'Authorization': 'Bearer ' + context.rootState.app.jwt,
              'Content-Type': 'application/json',
          },
        });

        if (response.status === 401) {
          context.commit('setUserAuthenticated', false);
          if (router.currentRoute.path !== '/') {
            router.push('/');
          }
          return;
        } else if (response.status === 408) {
          context.commit('toggleDialog', {
            viz: true,
            msg: 'Die Suche dauert zu lang, versuchen Sie es später noch einmal.',
          });
          context.commit('newItems', false);
          return;
        } else if (response.status >= 400) {
          context.commit('toggleDialog', {
            viz: true,
            msg: `Bei der Suche ist ein Fehler aufgetreten.
              Bitte Überprüfen Sie den Suchbefriff auf ungültige Zeichen.`,
          });
          context.commit('newItems', false);
          return;
        }
        const json = await response.json();
        // send list to vuex store
        if (json.list && json.list.length !== 0) {
          context.commit('populateList', json.list);
          context.commit('showDetail', json.list[0].src_id);
        } else {
          context.commit('populateList', []);
          context.commit('showDetail', null);
        }
        // refresh token
        if (json.token) {
          context.commit('setJWT', json.token);
        }
        // format services for path string
        if (body.services === null) {
          body.services = 'null';
        } else {
          body.services = body.services.join('~');
        }
        // assemble path string and compare to current route, pushing a new path triggers search
        const path = '/search/' + body.from + '/' + body.to + '/' + body.services + '/' + body.query;
        if (path !== router.currentRoute.path) {
          router.push(path);
        }
        clearTimeout(timeoutId);
        context.commit('newItems', false);
      } catch (err) {
        console.error(err);
        context.commit('toggleDialog', {
          viz: true,
          msg: 'Die Suche dauert zu lang, versuchen Sie es später noch einmal.',
        });
        context.commit('newItems', false);
      }
    },
    /**
     * Fetch the default list from EPD Archive API
     * @param context
     * @param source
     */
    async fetchList(context: any, source: any) {
      // console.log('fetchList -> source', source);
      if (context.rootState.app.jwt) {
        const controller = new AbortController();
        const signal = controller.signal;
        context.commit('newItems', true);

        try {
          // 22 second timeout, then cancel request
          const timeoutId = setTimeout(() => {
            controller.abort();
          }, 22000);

          const URL = process.env.VUE_APP_AUTH_URL;
          const response = await fetch(URL + '/api/list', {
            method: 'POST',
            credentials: 'include',
            signal,
            headers: {
              'Authorization': 'Bearer ' + context.rootState.app.jwt,
              'Content-Type': 'application/json',
            },
          });

          if (response.status === 401) {
            context.commit('setUserAuthenticated', false);
            if (router.currentRoute.path !== '/') {
              router.push('/');
            }
          }
          const json = await response.json();
          if ( !json.list || json.list.length === 0) { return; }
          context.commit('populateList', json.list);
          if (window.location.pathname.indexOf('detail/') < 0) {
            context.commit('showDetail', json.list[0].src_id);
          }
          context.commit('newItems', false);
          clearTimeout(timeoutId);
        } catch (err) {
          context.commit('toggleDialog', {
            viz: true,
            msg: 'Die Nachrichtenliste kann nicht geladen werden, versuchen Sie es später noch einmal.',
          });
          context.commit('newItems', false);
          console.log('fetchList -> err.message', err.message);
        }
      }
    },
    /**
     * Gets a limited list of results from the IDB storage and commits the result to
     * the populateList mutation.
     * @param context
     * @param data
     */
    updateList(context: any, data: any) {
      context.commit('updateProgress', 0);
    },
  },
};

const store: Store<any> = new Vuex.Store({
  modules: {
    app: appModule,
    list: listModule,
  },
  plugins: [vuexLocalforage.plugin],
});


export default store;
